import React, { useMemo, useState, useEffect, memo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Select, Flex, Typography, Row, Col, Space, Grid } from 'antd';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Trans, useTranslation } from 'react-i18next';

import { TrialForm } from '@Features/index';
import { AdvCard } from '@Widgets/index';
import { Horn, Avatar2 } from '@Shared/assets/images';
import {
    useLazyGetPlansQuery,
    useLazyGetWinbackPlansQuery,
    useLazyGetBlackFridayPlansQuery,
} from '@Entities/signup/api/signupSlice';
import { ga4event } from '@Shared/utils/ga4event';
import './styles.less';

const { Title, Paragraph } = Typography;
const { useBreakpoint } = Grid;

const customTokens = {
    colorSurfaceBlueBg: '#D4E8FF',
    colorSurfaceLightBlueCard: '#F2F7FF',
    colorButtonDefaultBlue: '#0085FF',
};

const styles: Record<string, React.CSSProperties> = {
    main: {
        maxWidth: 'min(calc(100vw - 20px), 1400px)',
        width: 'fit-content',
        height: 'fit-content',
        boxShadow: 'var(--ant-box-shadow)',
    },
    left: {
        padding: '40px 60px',
    },
    buttonsSpace: {
        width: '100%',
    },
    topLogo: {
        position: 'fixed',
        top: '25px',
        marginLeft: '-60px',
        width: 162,
    },
    orDivider: {
        margin: 10,
        textAlign: 'center',
    },
    em: {
        color: customTokens.colorButtonDefaultBlue,
        fontStyle: 'normal',
    },
    bg: {
        position: 'fixed',
        zIndex: -1,
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
    },
    trialContainer: {
        height: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
    },
};

export type PlanType = {
    value?: string;
    cycle?: string;
    title: string;
    trialDays: string;
    price: string;
    id: string;
    currency?: string;
};

const PlanLabel = ({ title, price, cycle, id, currency }: Omit<PlanType, 'trialDays'>) => {
    const breakpoints = useBreakpoint();
    const { t } = useTranslation('translation', { keyPrefix: 'signup' });
    const priceText =
        (currency
            ? new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency,
                  minimumFractionDigits: Number(price) % 1 === 0 ? 0 : 2,
              })
                  .format(Number(price))
                  .replace(',', '')
            : price.replace(',', '')) + `/${t(`trial_step.${cycle === 'yearly' ? 'year' : 'month'}`)}`;

    return (
        <h5
            className="xxlSelectOptionTitle"
            style={{ fontSize: breakpoints.md ? 20 : 16 }}
        >
            <div style={{ whiteSpace: 'break-spaces' }}>
                <span>{title}</span>{' '}
                {cycle === 'yearly' && <span className="annual-em">{t('trial_step.annual')} </span>}
                <span>—</span> <span>{priceText}</span>
            </div>
            {id.includes('propromo2024year') ? <div className="save-tag">{t('trial_step.save')} 40%</div> : null}
            {id.includes('enterprisepromo2024year') ? <div className="save-tag">{t('trial_step.save')} 50%</div> : null}

            {cycle === 'yearly' &&
            !['enterprisepromo2024year', 'propromo2024year'].some((subStr) => id.includes(subStr)) ? (
                <div className="save-tag">{t('trial_step.save')} 17%</div>
            ) : null}
        </h5>
    );
};

export const TrialStep = memo(({ handleStep }: { handleStep: (arg1: number) => void }) => {
    const [activePlan, setActivePlan] = useState<PlanType | null>(null);
    const { t } = useTranslation('translation', { keyPrefix: 'signup' });
    const location = useLocation();
    const [fetchPlans, { isLoading: isLoadingPlans, data: dataPlans }] = useLazyGetPlansQuery();
    const [fetchWinbackPlans, { isLoading: isLoadingWinbackPlans, data: dataWinbackPlans }] =
        useLazyGetWinbackPlansQuery();
    const [fetchBlackfridayPlans, { isLoading: isLoadingBlackFridayPlans, data: dataBlackFridayPlans }] =
        useLazyGetBlackFridayPlansQuery();
    const [searchParams] = useSearchParams();
    const breakpoints = useBreakpoint();

    const isWinback = useMemo(() => location.pathname.includes('lovely-user/winback'), [location.pathname]);
    const isBlackfriday = useMemo(() => location.pathname.includes('blackfriday'), [location.pathname]);

    const planOptions: PlanType[] = useMemo(
        () => (isWinback ? dataWinbackPlans : isBlackfriday ? dataBlackFridayPlans : dataPlans)?.plans || [],
        [dataPlans, dataWinbackPlans, dataBlackFridayPlans, isBlackfriday, isWinback],
    );

    useEffect(() => {
        if (isWinback) {
            fetchWinbackPlans('');
        } else if (isBlackfriday) {
            fetchBlackfridayPlans('');
        } else {
            fetchPlans('');
        }
    }, [isWinback, isBlackfriday, fetchPlans, fetchWinbackPlans, fetchBlackfridayPlans]);

    const stripePromise = loadStripe(process.env.STRIPE_API_KEY_PUBLISHABLE || '');

    const chargeDate = useMemo(() => {
        const date = new Date();
        return new Date(
            date.setDate(date.getDate() + parseInt(activePlan?.trialDays || '14', 10)),
        ).toLocaleDateString();
    }, [activePlan]);

    const handlePlanChange = (newValue: unknown) => {
        const newPlan = planOptions.find(({ id }) => id === newValue)!;
        setActivePlan(newPlan);
        ga4event({
            event_name: 'send_advertiser_payment_method_select_plan',
            event_option1: 'advertiser registration funnel',
            event_option2: 'onboarding;select_plan',
            event_option4: JSON.stringify(newPlan),
        });
    };

    useEffect(() => {
        if (planOptions?.length) {
            const pid = searchParams.get('pid');
            if (pid) {
                const plan = planOptions.find((p) => p.id.startsWith(pid)) ?? null;
                setActivePlan(plan);
            } else {
                const proMonthlyPlan = planOptions.find(
                    (p) => ['Pro', 'Scale'].includes(p.title) && p.cycle === 'monthly',
                );
                if (proMonthlyPlan) {
                    setActivePlan(proMonthlyPlan);
                } else {
                    setActivePlan(planOptions[0]);
                }
            }
        }
        ga4event({
            event_name: 'send_advertiser_payment_method_select_plan',
            event_option1: 'advertiser registration funnel',
            event_option2: 'onboarding;select_plan',
            event_option4: JSON.stringify(planOptions[0]),
        });
    }, [planOptions, searchParams]);

    const planOptionsWithLabel = useMemo(
        () =>
            planOptions.map(({ title, price, cycle, id, currency }) => ({
                value: id,
                label: (
                    <PlanLabel
                        title={title}
                        price={price}
                        cycle={cycle}
                        id={id}
                        currency={currency}
                    />
                ),
            })),
        [planOptions],
    );

    const isLoading = isLoadingPlans || isLoadingWinbackPlans || isLoadingBlackFridayPlans;

    return (
        <Row style={styles.main}>
            <Col
                lg={14}
                md={44}
                sm={24}
                xs={24}
            >
                <div style={{ ...styles.trialContainer, padding: breakpoints.lg ? '40px 60px' : 20 }}>
                    <Title
                        level={4}
                        style={{ marginBottom: 20 }}
                    >
                        {isBlackfriday
                            ? t('trial_step.title_blackfriday', { trialDays: activePlan?.trialDays || '14' })
                            : t('trial_step.title', { trialDays: activePlan?.trialDays || '14' })}
                    </Title>

                    <Space
                        direction="vertical"
                        style={{
                            background: customTokens.colorSurfaceLightBlueCard,
                            borderRadius: 'var(--ant-border-radius-lg)',
                        }}
                    >
                        <Select
                            value={activePlan?.id || ''}
                            size="large"
                            className="xxlSelect"
                            options={planOptionsWithLabel}
                            onChange={handlePlanChange}
                            loading={isLoading}
                            listHeight={400}
                        />

                        <Flex
                            gap="large"
                            align="flex-start"
                            style={{
                                padding: '16px 20px',
                            }}
                        >
                            <img
                                width={34}
                                src={Horn}
                                alt="horn"
                            />
                            <Paragraph>
                                <Trans
                                    i18nKey="signup.trial_step.plan_warning"
                                    values={{ chargeDate }}
                                />
                            </Paragraph>
                        </Flex>
                    </Space>
                    <Paragraph>&nbsp;</Paragraph>
                    <Elements stripe={stripePromise}>
                        <TrialForm
                            handleStep={handleStep}
                            activePlan={activePlan!}
                            isPlansLoading={isLoading}
                        />
                    </Elements>
                </div>
            </Col>

            <Col
                lg={10}
                md={0}
                sm={0}
                xs={0}
            >
                <AdvCard
                    avatar={Avatar2}
                    author={t('trial_step.adv_card.author')}
                    authorPosition={t('trial_step.adv_card.author_position')}
                >
                    {t('trial_step.adv_card.text.0')} <em style={styles.em}>{t('trial_step.adv_card.text.1')}</em>
                    {t('trial_step.adv_card.text.2')} <em style={styles.em}>{t('trial_step.adv_card.text.3')}</em>
                    {t('trial_step.adv_card.text.4')}
                </AdvCard>
            </Col>
        </Row>
    );
});

TrialStep.displayName = 'TrialStep';
